import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Button } from '../components/ui/Button'
import { Label } from '../components/ui/label'
import toast from 'react-hot-toast'
import { getRememberMePreference, setRememberMePreference } from '../lib/storageAdapter'
import { APP_NAME } from '../lib/config'

const Login: React.FC = () => {
  const { signIn, user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(() => getRememberMePreference())
  const [loading, setLoading] = useState(false)

  // Load saved preference on mount
  useEffect(() => {
    setRememberMe(getRememberMePreference())
  }, [])

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/', { replace: true })
    }
  }, [user, authLoading, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email || !password) {
      toast.error('Παρακαλώ συμπληρώστε όλα τα πεδία')
      return
    }

    setLoading(true)
    try {
      // Save the "Remember Me" preference before signing in
      setRememberMePreference(rememberMe)
      
      const { error } = await signIn(email, password)
      
      if (error) {
        toast.error(error.message || 'Σφάλμα σύνδεσης')
        setLoading(false)
      } else {
        toast.success('Επιτυχής σύνδεση!')
        // Redirect to dashboard after successful login
        navigate('/', { replace: true })
      }
    } catch (error) {
      toast.error('Απρόσμενο σφάλμα κατά τη σύνδεση')
      console.error('Login error:', error)
      setLoading(false)
    }
  }

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Φόρτωση...</p>
        </div>
      </div>
    )
  }

  // Don't render login form if already logged in (will redirect)
  if (user) {
    return null
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
          {/* Logo/Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img 
                src="/icon-512x512.png" 
                alt="TableIN Logo" 
                className="w-20 h-20 object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">{APP_NAME}</h1>
            <p className="text-muted-foreground">Σύστημα διαχείρισης εστιατορίου</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
                placeholder="your@email.com"
                disabled={loading}
                autoComplete="email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Κωδικός</Label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
                placeholder="••••••••"
                disabled={loading}
                autoComplete="current-password"
              />
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center space-x-2">
              <input
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => {
                  const checked = e.target.checked
                  setRememberMe(checked)
                  // Update preference immediately when toggled
                  setRememberMePreference(checked)
                }}
                disabled={loading}
                className="h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 cursor-pointer"
              />
              <Label 
                htmlFor="remember-me" 
                className="text-sm font-normal cursor-pointer text-foreground"
              >
                Να με θυμάσαι
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              size="lg"
            >
              {loading ? 'Σύνδεση...' : 'Σύνδεση'}
            </Button>
          </form>

         
        </div>
      </div>
    </div>
  )
}

export default Login

